import React, { useEffect, useState, useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Table,
  FormFeedback,
} from 'reactstrap';
import Select from 'react-select';
import { useFormik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { FormType } from 'components/Modals/formTypes';
import { GenericFormModal } from 'components/Modals/AddGenericModal';
import { customSelectStyles } from 'components/Common/SelectStyle';
import Breadcrumb from 'components/Common/Breadcrumb';
import { useNavigate } from 'react-router-dom';
import {
  useCreateMutation,
  useUpdatePatchMutation,
  useDetailQuery,
  usePaginatedListQuery,
  useListQuery,
} from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { useShop } from 'context/shopContext';
import { handleFormErrors } from 'utils/formErrorHandler';
import { SHOP_PATHS } from 'routes/routesConstants';
import { useDebounce } from 'hooks/useDebounce';

interface ItemRow {
  id?: string;
  shopItemId: string;
  selectedValue?: { label: string; value: string } | null;
  itemUnitId: string | null;
  manufacturerName: string;
  batchNumber: string;
  packing: string;
  piecesInPacking: number | string;
  quantity: number | string;
  bonusQuantity: number | string;
  discountPercentage: number | string;
  taxAmount: number | string;
  netValue: number | string;
  expiryDate: string;
  purchasePricePerUnit: number | string;
}

interface InvoiceData {
  invoiceNumber: string;
  invoiceDate: string;
  supplierName: string;
  supplierContact1: string;
  supplierContact2: string;
  bookedBy: string;
  deliveredBy: string;
  totalAmount: number;
  items: ItemRow[];
}

// Validation Schemas
const invoiceValidationSchema = Yup.object({
  invoiceNumber: Yup.string().trim().required('Invoice number is required').max(50),
  invoiceDate: Yup.date()
    .required('Invoice date is required')
    .max(new Date(), 'Invoice date cannot be in the future'),
  supplierName: Yup.string().trim().required('Supplier name is required').max(100),
  supplierContact1: Yup.string().trim().required('Supplier contact is required').max(50),
  supplierContact2: Yup.string().trim().max(50).nullable(),
  bookedBy: Yup.string().trim().max(100).nullable(),
  deliveredBy: Yup.string().trim().max(100).nullable(),
  totalAmount: Yup.number().min(0),
});

const itemValidationSchema = Yup.object({
  shopItemId: Yup.string().required('Medicine is required'),
  batchNumber: Yup.string().trim().required('Batch number is required').max(50),
  quantity: Yup.number().required('Quantity is required').min(1),
  purchasePricePerUnit: Yup.number().required('Purchase price is required').min(0.01),
  piecesInPacking: Yup.number()
    .min(1)
    .nullable()
    .transform((v, o) => (o === '' ? null : v)),
  bonusQuantity: Yup.number()
    .min(0)
    .nullable()
    .transform((v, o) => (o === '' ? null : v)),
  discountPercentage: Yup.number()
    .min(0)
    .max(100)
    .nullable()
    .transform((v, o) => (o === '' ? null : v)),
  taxAmount: Yup.number()
    .min(0)
    .nullable()
    .transform((v, o) => (o === '' ? null : v)),
  expiryDate: Yup.date().nullable().min(new Date(), 'Expiry date must be in the future'),
});

const PurchaseInvoiceEntry: React.FC<{ invoiceId?: string }> = ({ invoiceId }) => {
  const { shop } = useShop();
  const navigate = useNavigate();
  const isEditing = !!invoiceId;

  const [modalFormType, setModalFormType] = useState<FormType | null>(null);
  const [items, setItems] = useState<ItemRow[]>([]);
  const [itemErrors, setItemErrors] = useState<{ [key: number]: { [key: string]: string } }>({});
  const [itemTouched, setItemTouched] = useState<{ [key: number]: { [key: string]: boolean } }>({});

  // Search terms per row
  const [searchTerms, setSearchTerms] = useState<{ [key: number]: string }>({});

  // Get the latest search term for debouncing (single query)
  const activeSearchTerm = useMemo(() => {
    const values = Object.values(searchTerms).filter(Boolean);
    return values.length > 0 ? values[values.length - 1] : '';
  }, [searchTerms]);

  const debouncedSearchTerm = useDebounce(activeSearchTerm, 300);

  // Single top-level paginated query for medicines (fixes the hook error)
  const { data: medicineSearchData, isLoading: isSearchingMedicine } = usePaginatedListQuery(
    API_ENDPOINTS.TENANT.SHOP_ITEMS.BASE,
    {
      pageNumber: 1,
      pageSize: 50,
      search: debouncedSearchTerm,
      shopId: shop?.id,
    },
    { enabled: debouncedSearchTerm.length > 1 },
  );

  const medicineOptions = useMemo(() => {
    return (
      medicineSearchData?.items?.map((med: any) => ({
        label: `${med.name} (${med.sku || ''})`,
        value: med.id,
      })) || []
    );
  }, [medicineSearchData]);

  // Units
  const { data: fetchedUnits = [], isLoading: isLoadingUnits } = useListQuery(
    API_ENDPOINTS.TENANT.ITEM_UNITS.BY_CATEGORY,
    { category: shop?.category },
  );

  const unitSelectOptions = fetchedUnits.map((unit: any) => ({
    label: unit.name || unit.unitName,
    value: unit.id,
  }));

  // Invoice detail for edit mode
  const {
    data: invoice,
    isLoading: fetching,
    error: fetchError,
  } = useDetailQuery<InvoiceData>(API_ENDPOINTS.TENANT.PURCHASES.WITH_ID, { id: invoiceId || '' });

  // Mutations
  const { mutate: createMutation, isPending: createPending } = useCreateMutation(
    API_ENDPOINTS.TENANT.PURCHASES.BASE,
  );
  const { mutate: updateMutation, isPending: updatePending } = useUpdatePatchMutation(
    API_ENDPOINTS.TENANT.PURCHASES.PATCH,
    API_ENDPOINTS.TENANT.PURCHASES.BASE,
  );

  const getInitialValues = () => ({
    invoiceNumber: invoice?.invoiceNumber || '',
    invoiceDate: invoice?.invoiceDate
      ? new Date(invoice.invoiceDate).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0],
    supplierName: invoice?.supplierName || '',
    supplierContact1: invoice?.supplierContact1 || '',
    supplierContact2: invoice?.supplierContact2 || '',
    bookedBy: invoice?.bookedBy || '',
    deliveredBy: invoice?.deliveredBy || '',
    totalAmount: invoice?.totalAmount || 0,
  });

  const formik = useFormik<InvoiceData>({
    initialValues: getInitialValues(),
    validationSchema: invoiceValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values, formikHelpers: FormikHelpers<InvoiceData>) => {
      const validated = await validateAllItems();
      if (!validated) return;

      const totalAmount = items.reduce((sum, i) => sum + (Number(i.netValue) || 0), 0);

      if (values.totalAmount !== totalAmount) {
        formikHelpers.setFieldError(
          'totalAmount',
          'Total amount does not match with Total Net Amount of items',
        );
        return;
      }

      const payload = {
        ...(isEditing ? {} : { shopId: shop.id }),
        invoiceNumber: values.invoiceNumber.trim(),
        invoiceDate: values.invoiceDate,
        supplierName: values.supplierName.trim(),
        supplierContact1: values.supplierContact1.trim(),
        supplierContact2: values.supplierContact2?.trim() || null,
        bookedBy: values.bookedBy?.trim() || null,
        deliveredBy: values.deliveredBy?.trim() || null,
        totalAmount,
        items: items.map((item) => ({
          id: item.id,
          shopItemId: item.shopItemId,
          itemUnitId: item.itemUnitId || null,
          manufacturerName: item.manufacturerName || null,
          batchNumber: item.batchNumber,
          packing: item.packing || null,
          piecesInPacking: item.piecesInPacking ? Number(item.piecesInPacking) : null,
          quantity: Number(item.quantity),
          bonusQuantity: Number(item.bonusQuantity) || 0,
          discountPercentage: item.discountPercentage ? Number(item.discountPercentage) : null,
          taxAmount: item.taxAmount ? Number(item.taxAmount) : null,
          netValue: Number(item.netValue),
          expiryDate: item.expiryDate || null,
          purchasePricePerUnit: Number(item.purchasePricePerUnit),
        })),
      };

      const onSuccess = () => {
        navigate(SHOP_PATHS.PURCHASE_INVOICES.replace(':shopId', shop.id || ''));
      };

      const onError = (error: any) => handleFormErrors<InvoiceData>(error, formikHelpers);

      if (isEditing) {
        updateMutation({ data: payload, params: { id: invoiceId } }, { onSuccess, onError });
      } else {
        createMutation(payload, { onSuccess, onError });
      }
    },
  });

  // Populate items when editing
  useEffect(() => {
    if (invoice?.items?.length) {
      setItems(
        invoice.items.map((i: any) => ({
          id: i.id,
          selectedValue: { label: i.shopItem.name, value: i.shopItemId } || null,
          shopItemId: i.shopItemId || '',
          itemUnitId: i.itemUnitId || null,
          manufacturerName: i.manufacturerName || '',
          batchNumber: i.batchNumber || '',
          packing: i.packing || '',
          piecesInPacking: i.piecesInPacking?.toString() || '',
          quantity: i.quantity?.toString() || '',
          bonusQuantity: i.bonusQuantity?.toString() || '',
          discountPercentage: i.discountPercentage?.toString() || '',
          taxAmount: i.taxAmount?.toString() || '',
          netValue: i.netValue?.toString() || '',
          purchasePricePerUnit: i.purchasePricePerUnit?.toString() || '',
          expiryDate: i.expiryDate ? new Date(i.expiryDate).toISOString().split('T')[0] : '',
        })),
      );
    }
  }, [invoice]);

  // Add initial row for new invoice
  useEffect(() => {
    if (items.length === 0 && !isEditing) {
      addNewRow();
    }
  }, [isEditing]);

  const addNewRow = () => {
    setItems((prev) => [
      ...prev,
      {
        shopItemId: '',
        itemUnitId: null,
        manufacturerName: '',
        batchNumber: '',
        packing: '',
        piecesInPacking: '',
        quantity: '',
        bonusQuantity: '',
        discountPercentage: '',
        taxAmount: '',
        netValue: '',
        expiryDate: '',
        purchasePricePerUnit: '',
      },
    ]);
  };

  const removeRow = (index: number) => {
    if (items.length <= 1) return;

    setItems((prev) => prev.filter((_, i) => i !== index));

    setItemErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return Object.fromEntries(
        Object.entries(newErrors).map(([k, v]) => [
          Number(k) > index ? Number(k) - 1 : Number(k),
          v,
        ]),
      );
    });

    setItemTouched((prev) => {
      const newTouched = { ...prev };
      delete newTouched[index];
      return Object.fromEntries(
        Object.entries(newTouched).map(([k, v]) => [
          Number(k) > index ? Number(k) - 1 : Number(k),
          v,
        ]),
      );
    });

    setSearchTerms((prev) => {
      const newTerms = { ...prev };
      delete newTerms[index];
      return newTerms;
    });
  };

  const handleItemChange = (index: number, field: keyof ItemRow, value: any) => {
    setItems((prev) => {
      const updated = [...prev];
      const item = { ...updated[index], [field]: value };

      const qty = Number(item.quantity) || 0;
      const pieces = Number(item.piecesInPacking) || 0;
      const rate = Number(item.purchasePricePerUnit) || 0;
      const gross = pieces * rate;
      const discount = (gross * (Number(item.discountPercentage) || 0)) / 100;
      const tax = Number(item.taxAmount) || 0;
      item.netValue = (gross - discount + tax).toFixed(2);

      const found = medicineOptions.find((opt) => opt.value === item.shopItemId);

      item.selectedValue = found ?? item.selectedValue ?? null;

      updated[index] = item;
      return updated;
    });

    validateItem(index);
  };

  const handleMedicineSearchChange = (index: number, inputValue: string) => {
    setSearchTerms((prev) => ({ ...prev, [index]: inputValue }));
  };

  const handleMedicineSelect = (index: number, selected: { value: string } | null) => {
    handleItemChange(index, 'shopItemId', selected?.value || '');
    // Clear search term after selection
    setSearchTerms((prev) => {
      const nt = { ...prev };
      delete nt[index];
      return nt;
    });
  };

  const handleItemBlur = (index: number, field: keyof ItemRow) => {
    setItemTouched((prev) => ({
      ...prev,
      [index]: { ...(prev[index] || {}), [field]: true },
    }));
    validateItem(index);
  };

  const validateItem = async (index: number) => {
    try {
      await itemValidationSchema.validate(items[index], { abortEarly: false });
      setItemErrors((prev) => {
        const ne = { ...prev };
        delete ne[index];
        return ne;
      });
    } catch (err: any) {
      if (err.inner) {
        const errs: { [key: string]: string } = {};
        err.inner.forEach((e: any) => {
          if (e.path) errs[e.path] = e.message;
        });
        setItemErrors((prev) => ({ ...prev, [index]: errs }));
      }
    }
  };

  const validateAllItems = async (): Promise<boolean> => {
    let hasErrors = false;
    const errors: { [key: number]: { [key: string]: string } } = {};

    for (let i = 0; i < items.length; i++) {
      try {
        await itemValidationSchema.validate(items[i], { abortEarly: false });
      } catch (err: any) {
        hasErrors = true;
        if (err.inner) {
          const itemErr: { [key: string]: string } = {};
          err.inner.forEach((e: any) => {
            if (e.path) itemErr[e.path] = e.message;
          });
          errors[i] = itemErr;
        }
      }
    }

    setItemErrors(errors);
    if (hasErrors) alert('Please fix validation errors in the items table');
    return !hasErrors;
  };

  const hasItemError = (rowIndex: number, field: keyof ItemRow): boolean => {
    return !!itemTouched[rowIndex]?.[field] && !!itemErrors[rowIndex]?.[field];
  };

  const getSelectStyles = (hasError: boolean) => ({
    ...customSelectStyles,
    control: (base: any) => ({
      ...customSelectStyles.control(base),
      borderColor: hasError ? '#dc3545' : base.borderColor,
      '&:hover': { borderColor: hasError ? '#dc3545' : base.borderColor },
    }),
  });

  if (fetching) {
    return (
      <div className="page-content">
        <Container fluid className="mt-4">
          Loading invoice details...
        </Container>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="page-content">
        <Container fluid className="mt-4">
          Error loading invoice: {fetchError.message}
        </Container>
      </div>
    );
  }

  return (
    <div>
      <Container fluid className="mt-4">
        <Breadcrumb
          title="Inventory"
          breadcrumbItem={isEditing ? 'Edit Purchase Invoice' : 'Purchase Invoices Entry'}
        />

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <Form onSubmit={formik.handleSubmit}>
                  <Row>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Invoice Number *</Label>
                        <Input
                          type="text"
                          name="invoiceNumber"
                          value={formik.values.invoiceNumber}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="e.g. INV-00123"
                          invalid={formik.touched.invoiceNumber && !!formik.errors.invoiceNumber}
                        />
                        <FormFeedback>{formik.errors.invoiceNumber}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Date *</Label>
                        <Input
                          type="date"
                          name="invoiceDate"
                          value={formik.values.invoiceDate}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.invoiceDate && !!formik.errors.invoiceDate}
                        />
                        <FormFeedback>{formik.errors.invoiceDate}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Supplier Name *</Label>
                        <Input
                          type="text"
                          name="supplierName"
                          value={formik.values.supplierName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="Supplier name"
                          invalid={formik.touched.supplierName && !!formik.errors.supplierName}
                        />
                        <FormFeedback>{formik.errors.supplierName}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Supplier Contact 1 *</Label>
                        <Input
                          type="text"
                          name="supplierContact1"
                          value={formik.values.supplierContact1}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="Primary contact"
                          invalid={
                            formik.touched.supplierContact1 && !!formik.errors.supplierContact1
                          }
                        />
                        <FormFeedback>{formik.errors.supplierContact1}</FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Supplier Contact 2</Label>
                        <Input
                          type="text"
                          name="supplierContact2"
                          value={formik.values.supplierContact2}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="Secondary contact"
                          invalid={
                            formik.touched.supplierContact2 && !!formik.errors.supplierContact2
                          }
                        />
                        <FormFeedback>{formik.errors.supplierContact2}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Booked By</Label>
                        <Input
                          type="text"
                          name="bookedBy"
                          value={formik.values.bookedBy}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="Person who booked"
                          invalid={formik.touched.bookedBy && !!formik.errors.bookedBy}
                        />
                        <FormFeedback>{formik.errors.bookedBy}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Delivered By</Label>
                        <Input
                          type="text"
                          name="deliveredBy"
                          value={formik.values.deliveredBy}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="Person who delivered"
                          invalid={formik.touched.deliveredBy && !!formik.errors.deliveredBy}
                        />
                        <FormFeedback>{formik.errors.deliveredBy}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Total Amount</Label>
                        <Input
                          type="number"
                          name="totalAmount"
                          value={formik.values.totalAmount}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="Total amount"
                          invalid={formik.touched.totalAmount && !!formik.errors.totalAmount}
                        />
                        <FormFeedback>{formik.errors.totalAmount}</FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>

                  <h5 className="mb-3 mt-4">Items</h5>
                  <Row className="align-items-center mb-3">
                    <Col xs="6">
                      <Button color="secondary" onClick={addNewRow} type="button">
                        + Add Item
                      </Button>
                    </Col>
                    <Col xs="6" className="text-end">
                      <Button
                        color="success"
                        onClick={() => setModalFormType(FormType.MEDICINE)}
                        type="button"
                      >
                        + Add Medicine
                      </Button>
                    </Col>
                  </Row>

                  <div style={{ overflowX: 'auto', maxHeight: '500px', minHeight: '200px' }}>
                    <Table bordered responsive size="sm" className="table-sticky">
                      <thead
                        style={{ position: 'sticky', top: 0, background: '#f7f7f7', zIndex: 1 }}
                      >
                        <tr>
                          <th style={{ minWidth: '250px' }}>Medicine *</th>
                          <th style={{ minWidth: '150px' }}>Unit</th>
                          <th style={{ minWidth: '150px' }}>Manufacturer</th>
                          <th style={{ minWidth: '120px' }}>Batch No. *</th>
                          <th style={{ minWidth: '120px' }}>Packing</th>
                          <th style={{ minWidth: '100px' }}>Pieces/Pack</th>
                          <th style={{ minWidth: '100px' }}>Qty (Packs) *</th>
                          <th style={{ minWidth: '100px' }}>Bonus Qty</th>
                          <th style={{ minWidth: '120px' }}>Purchase Price/Unit *</th>
                          <th style={{ minWidth: '100px' }}>Discount %</th>
                          <th style={{ minWidth: '100px' }}>Tax Amount</th>
                          <th style={{ minWidth: '120px' }}>Net Value</th>
                          <th style={{ minWidth: '120px' }}>Expiry Date</th>
                          <th style={{ minWidth: '80px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, idx) => (
                          <tr key={idx}>
                            {/* Medicine - Debounced Search */}
                            <td style={{ padding: 0 }}>
                              <Select
                                value={item.selectedValue || null}
                                onChange={(sel) => handleMedicineSelect(idx, sel)}
                                onInputChange={(val) => handleMedicineSearchChange(idx, val)}
                                options={medicineOptions}
                                isLoading={isSearchingMedicine}
                                isSearchable
                                isClearable
                                placeholder="Search and select medicine..."
                                styles={getSelectStyles(hasItemError(idx, 'shopItemId'))}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                noOptionsMessage={() =>
                                  isSearchingMedicine
                                    ? 'Searching...'
                                    : 'Type to search medicines...'
                                }
                              />
                              {hasItemError(idx, 'shopItemId') && (
                                <FormFeedback style={{ display: 'block', fontSize: '0.75rem' }}>
                                  {itemErrors[idx]?.shopItemId}
                                </FormFeedback>
                              )}
                            </td>

                            {/* Unit */}
                            <td style={{ padding: 0 }}>
                              <Select
                                value={
                                  unitSelectOptions.find((opt) => opt.value === item.itemUnitId) ||
                                  null
                                }
                                onChange={(sel) =>
                                  handleItemChange(idx, 'itemUnitId', sel?.value || null)
                                }
                                onBlur={() => handleItemBlur(idx, 'itemUnitId')}
                                options={unitSelectOptions}
                                isLoading={isLoadingUnits}
                                isClearable
                                placeholder="Select Unit"
                                styles={getSelectStyles(hasItemError(idx, 'itemUnitId'))}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                              />
                              {hasItemError(idx, 'itemUnitId') && (
                                <FormFeedback style={{ display: 'block', fontSize: '0.75rem' }}>
                                  {itemErrors[idx]?.itemUnitId}
                                </FormFeedback>
                              )}
                            </td>

                            {/* Manufacturer */}
                            <td style={{ padding: 0 }}>
                              <Input
                                style={{
                                  border: 'none',
                                  height: '100%',
                                  width: '100%',
                                  padding: '0 8px',
                                  fontSize: '0.875rem',
                                }}
                                value={item.manufacturerName}
                                onChange={(e) =>
                                  handleItemChange(idx, 'manufacturerName', e.target.value)
                                }
                                onBlur={() => handleItemBlur(idx, 'manufacturerName')}
                                bsSize="sm"
                              />
                              {hasItemError(idx, 'manufacturerName') && (
                                <FormFeedback style={{ display: 'block', fontSize: '0.75rem' }}>
                                  {itemErrors[idx]?.manufacturerName}
                                </FormFeedback>
                              )}
                            </td>

                            {/* Batch Number */}
                            <td style={{ padding: 0 }}>
                              <Input
                                style={{
                                  border: 'none',
                                  height: '100%',
                                  width: '100%',
                                  padding: '0 8px',
                                  fontSize: '0.875rem',
                                }}
                                value={item.batchNumber}
                                onChange={(e) =>
                                  handleItemChange(idx, 'batchNumber', e.target.value)
                                }
                                onBlur={() => handleItemBlur(idx, 'batchNumber')}
                                bsSize="sm"
                              />
                              {hasItemError(idx, 'batchNumber') && (
                                <FormFeedback style={{ display: 'block', fontSize: '0.75rem' }}>
                                  {itemErrors[idx]?.batchNumber}
                                </FormFeedback>
                              )}
                            </td>

                            {/* Packing */}
                            <td style={{ padding: 0 }}>
                              <Input
                                style={{
                                  border: 'none',
                                  height: '100%',
                                  width: '100%',
                                  padding: '0 8px',
                                  fontSize: '0.875rem',
                                }}
                                value={item.packing}
                                onChange={(e) => handleItemChange(idx, 'packing', e.target.value)}
                                onBlur={() => handleItemBlur(idx, 'packing')}
                                bsSize="sm"
                              />
                              {hasItemError(idx, 'packing') && (
                                <FormFeedback style={{ display: 'block', fontSize: '0.75rem' }}>
                                  {itemErrors[idx]?.packing}
                                </FormFeedback>
                              )}
                            </td>

                            {/* Pieces/Pack */}
                            <td style={{ padding: 0 }}>
                              <Input
                                type="number"
                                style={{
                                  border: 'none',
                                  height: '100%',
                                  width: '100%',
                                  padding: '0 8px',
                                  fontSize: '0.875rem',
                                }}
                                min={1}
                                value={item.piecesInPacking}
                                onChange={(e) =>
                                  handleItemChange(idx, 'piecesInPacking', e.target.value)
                                }
                                onBlur={() => handleItemBlur(idx, 'piecesInPacking')}
                                bsSize="sm"
                              />
                              {hasItemError(idx, 'piecesInPacking') && (
                                <FormFeedback style={{ display: 'block', fontSize: '0.75rem' }}>
                                  {itemErrors[idx]?.piecesInPacking}
                                </FormFeedback>
                              )}
                            </td>

                            {/* Quantity */}
                            <td style={{ padding: 0 }}>
                              <Input
                                type="number"
                                style={{
                                  border: 'none',
                                  height: '100%',
                                  width: '100%',
                                  padding: '0 8px',
                                  fontSize: '0.875rem',
                                }}
                                min={1}
                                value={item.quantity}
                                onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                                onBlur={() => handleItemBlur(idx, 'quantity')}
                                bsSize="sm"
                              />
                              {hasItemError(idx, 'quantity') && (
                                <FormFeedback style={{ display: 'block', fontSize: '0.75rem' }}>
                                  {itemErrors[idx]?.quantity}
                                </FormFeedback>
                              )}
                            </td>

                            {/* Bonus Qty */}
                            <td style={{ padding: 0 }}>
                              <Input
                                type="number"
                                style={{
                                  border: 'none',
                                  height: '100%',
                                  width: '100%',
                                  padding: '0 8px',
                                  fontSize: '0.875rem',
                                }}
                                min={0}
                                value={item.bonusQuantity}
                                onChange={(e) =>
                                  handleItemChange(idx, 'bonusQuantity', e.target.value)
                                }
                                onBlur={() => handleItemBlur(idx, 'bonusQuantity')}
                                bsSize="sm"
                              />
                              {hasItemError(idx, 'bonusQuantity') && (
                                <FormFeedback style={{ display: 'block', fontSize: '0.75rem' }}>
                                  {itemErrors[idx]?.bonusQuantity}
                                </FormFeedback>
                              )}
                            </td>

                            {/* Purchase Price */}
                            <td style={{ padding: 0 }}>
                              <Input
                                type="number"
                                step="0.01"
                                style={{
                                  border: 'none',
                                  height: '100%',
                                  width: '100%',
                                  padding: '0 8px',
                                  fontSize: '0.875rem',
                                }}
                                min={0.01}
                                value={item.purchasePricePerUnit}
                                onChange={(e) =>
                                  handleItemChange(idx, 'purchasePricePerUnit', e.target.value)
                                }
                                onBlur={() => handleItemBlur(idx, 'purchasePricePerUnit')}
                                bsSize="sm"
                              />
                              {hasItemError(idx, 'purchasePricePerUnit') && (
                                <FormFeedback style={{ display: 'block', fontSize: '0.75rem' }}>
                                  {itemErrors[idx]?.purchasePricePerUnit}
                                </FormFeedback>
                              )}
                            </td>

                            {/* Discount % */}
                            <td style={{ padding: 0 }}>
                              <Input
                                type="number"
                                style={{
                                  border: 'none',
                                  height: '100%',
                                  width: '100%',
                                  padding: '0 8px',
                                  fontSize: '0.875rem',
                                }}
                                min={0}
                                max={100}
                                value={item.discountPercentage}
                                onChange={(e) =>
                                  handleItemChange(idx, 'discountPercentage', e.target.value)
                                }
                                onBlur={() => handleItemBlur(idx, 'discountPercentage')}
                                bsSize="sm"
                              />
                              {hasItemError(idx, 'discountPercentage') && (
                                <FormFeedback style={{ display: 'block', fontSize: '0.75rem' }}>
                                  {itemErrors[idx]?.discountPercentage}
                                </FormFeedback>
                              )}
                            </td>

                            {/* Tax Amount */}
                            <td style={{ padding: 0 }}>
                              <Input
                                type="number"
                                style={{
                                  border: 'none',
                                  height: '100%',
                                  width: '100%',
                                  padding: '0 8px',
                                  fontSize: '0.875rem',
                                }}
                                min={0}
                                value={item.taxAmount}
                                onChange={(e) => handleItemChange(idx, 'taxAmount', e.target.value)}
                                onBlur={() => handleItemBlur(idx, 'taxAmount')}
                                bsSize="sm"
                              />
                              {hasItemError(idx, 'taxAmount') && (
                                <FormFeedback style={{ display: 'block', fontSize: '0.75rem' }}>
                                  {itemErrors[idx]?.taxAmount}
                                </FormFeedback>
                              )}
                            </td>

                            {/* Net Value */}
                            <td style={{ padding: 0 }}>
                              <Input
                                type="number"
                                style={{
                                  border: 'none',
                                  height: '100%',
                                  width: '100%',
                                  padding: '0 8px',
                                  fontSize: '0.875rem',
                                  backgroundColor: '#f0f0f0',
                                }}
                                readOnly
                                value={item.netValue}
                                bsSize="sm"
                              />
                            </td>

                            {/* Expiry Date */}
                            <td style={{ padding: 0 }}>
                              <Input
                                type="date"
                                style={{
                                  border: 'none',
                                  height: '100%',
                                  width: '100%',
                                  padding: '0 8px',
                                  fontSize: '0.875rem',
                                }}
                                value={item.expiryDate}
                                onChange={(e) =>
                                  handleItemChange(idx, 'expiryDate', e.target.value)
                                }
                                onBlur={() => handleItemBlur(idx, 'expiryDate')}
                                bsSize="sm"
                              />
                              {hasItemError(idx, 'expiryDate') && (
                                <FormFeedback style={{ display: 'block', fontSize: '0.75rem' }}>
                                  {itemErrors[idx]?.expiryDate}
                                </FormFeedback>
                              )}
                            </td>

                            {/* Action */}
                            <td className="text-center" style={{ padding: '4px' }}>
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => removeRow(idx)}
                                disabled={items.length <= 1}
                                type="button"
                              >
                                ✕
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <Row className="mt-3 justify-content-end">
                    <Col xs="12" sm="8" md="6" lg="4">
                      <CardBody className="py-2 px-3">
                        <div className="d-flex justify-content-between mb-2">
                          <strong>Total Purchased Items:</strong>
                          <span>{items.length}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <strong>Total Net Amount:</strong>
                          <span>
                            {items.reduce((sum, i) => sum + Number(i.netValue || 0), 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <strong>Total Tax:</strong>
                          <span>
                            {items.reduce((sum, i) => sum + Number(i.taxAmount || 0), 0).toFixed(2)}
                          </span>
                        </div>
                      </CardBody>
                    </Col>
                  </Row>

                  <div className="mt-4 text-end">
                    <Button
                      color="primary"
                      type="submit"
                      disabled={isEditing ? updatePending : createPending}
                    >
                      {isEditing ? 'Update Invoice' : 'Save Invoice'}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <GenericFormModal
          isOpen={modalFormType !== null}
          toggle={() => setModalFormType(null)}
          formType={modalFormType as FormType}
          onSuccess={() => setModalFormType(null)}
        />
      </Container>
    </div>
  );
};

export default PurchaseInvoiceEntry;
